import React, { useMemo, useState, useRef, useEffect } from 'react';
import { TrendingUp, TrendingDown, Minus, Users, Clock, Target, CheckCircle, AlertTriangle, Calendar, DollarSign, Archive, Play, Pause, CalendarDays, ChevronDown, X, Shield, AlertCircle, HelpCircle } from 'lucide-react';
import { useDashboard } from '../context/DashboardContext';
import ProjectModal from './ProjectModal';

// Custom MultiSelect Component
const MultiSelect = ({ label, options, selectedValues, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectAll = () => {
    if (selectedValues.length === options.length) {
      onChange([]);
    } else {
      onChange(options);
    }
  };

  const handleToggleOption = (option) => {
    if (selectedValues.includes(option)) {
      onChange(selectedValues.filter(item => item !== option));
    } else {
      onChange([...selectedValues, option]);
    }
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const displayText = selectedValues.length === 0 
    ? placeholder 
    : selectedValues.length === options.length 
    ? `All ${label}s (${selectedValues.length})`
    : `${selectedValues.length} ${label}${selectedValues.length === 1 ? '' : 's'} selected`;

  return (
    <div className="filter-group">
      <label>{label}:</label>
      <div className="multi-select-container" ref={dropdownRef}>
        <button
          className="multi-select-button"
          onClick={() => setIsOpen(!isOpen)}
          type="button"
        >
          <span className="multi-select-text">{displayText}</span>
          <ChevronDown size={16} className={`chevron ${isOpen ? 'open' : ''}`} />
        </button>
        
        {isOpen && (
          <div className="multi-select-dropdown">
            <div className="multi-select-header">
              <button
                className="select-all-button"
                onClick={handleSelectAll}
                type="button"
              >
                {selectedValues.length === options.length ? 'Deselect All' : 'Select All'}
              </button>
              {selectedValues.length > 0 && (
                <button
                  className="clear-all-button"
                  onClick={handleClearAll}
                  type="button"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            
            <div className="multi-select-options">
              {options.map((option) => (
                <label key={option} className="multi-select-option">
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(option)}
                    onChange={() => handleToggleOption(option)}
                  />
                  <span className="option-text">{option}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const MetricsOverview = () => {
  const { dashboardData, loading } = useDashboard();
  const [projectTypeFilter, setProjectTypeFilter] = useState([]);
  const [projectSubtypeFilter, setProjectSubtypeFilter] = useState([]);
  const [modalState, setModalState] = useState({
    isOpen: false,
    title: '',
    projects: [],
    metricType: ''
  });

  // Helper function to get field value by fieldLabel
  const getFieldValueByLabel = (project, fieldLabel) => {
    const field = project.fields?.find(f => f.fieldLabel === fieldLabel);
    return field?.fieldValueLabel;
  };

  // Get unique filter options from all projects
  const projectTypes = useMemo(() => {
    if (!dashboardData?.details?.projects) return [];
    return [...new Set(dashboardData.details.projects.map(p => getFieldValueByLabel(p, 'Project Type')).filter(Boolean))];
  }, [dashboardData?.details?.projects]);

  const projectSubtypes = useMemo(() => {
    if (!dashboardData?.details?.projects) return [];
    return [...new Set(dashboardData.details.projects.map(p => getFieldValueByLabel(p, 'Project Subtype')).filter(Boolean))];
  }, [dashboardData?.details?.projects]);



  // Helper function to get field value by fieldId
  const getFieldValue = (project, fieldId) => {
    const field = project.fields?.find(f => f.fieldId === fieldId);
    return field?.fieldValue;
  };

  // Helper function to check if date is in quarter
  const isDateInQuarter = (project, quarter, year) => {
    const goLiveDate = getFieldValue(project, 1601377); // Services Go Live Date
    if (!goLiveDate) return false;
    
    const date = new Date(goLiveDate);
    if (date.getFullYear() !== year) return false;
    
    const month = date.getMonth() + 1;
    switch (quarter) {
      case 1: return month >= 1 && month <= 3;
      case 2: return month >= 4 && month <= 6;
      case 3: return month >= 7 && month <= 9;
      case 4: return month >= 10 && month <= 12;
      default: return false;
    }
  };

  // Helper function to check if project is past due
  const isProjectPastDue = (project) => {
    const status = project.status?.label;
    if (status !== 'In progress') return false;
    
    const goLiveDate = getFieldValue(project, 1601377);
    if (!goLiveDate) return false;
    
    const date = new Date(goLiveDate);
    const now = new Date();
    return date < now;
  };

  // Filter projects based on selected filters
  const filteredProjects = useMemo(() => {
    if (!dashboardData?.details?.projects) return [];

    const projects = dashboardData.details.projects;
    
    return projects.filter(project => {
      // Project Type filter
      if (projectTypeFilter.length > 0) {
        const projectType = getFieldValueByLabel(project, 'Project Type');
        if (!projectType || !projectTypeFilter.includes(projectType)) return false;
      }
      
      // Project Subtype filter
      if (projectSubtypeFilter.length > 0) {
        const projectSubtype = getFieldValueByLabel(project, 'Project Subtype');
        if (!projectSubtype || !projectSubtypeFilter.includes(projectSubtype)) return false;
      }
      
      return true;
    });
  }, [dashboardData?.details?.projects, projectTypeFilter, projectSubtypeFilter]);

  // Current year for quarter calculations
  const currentYear = new Date().getFullYear();

  // Memoize metrics data calculation to prevent unnecessary re-computations
  const metricsData = useMemo(() => {
    if (!dashboardData?.details?.projects || !filteredProjects.length) return [];

    // Calculate metrics using filtered projects
    const totalProjects = filteredProjects.length;
    const closedProjects = filteredProjects.filter(p => p.status?.label === 'Completed').length;
    const pushedProjects = filteredProjects.filter(p => p.status?.label === 'Go-Live Pushed').length;
    const activeProjects = filteredProjects.filter(p => p.status?.label === 'In progress').length;
    const notStartedProjects = filteredProjects.filter(p => p.status?.label === 'Staging').length;
    const pastDueProjects = filteredProjects.filter(p => isProjectPastDue(p)).length;
    
    const q1Projects = filteredProjects.filter(p => isDateInQuarter(p, 1, currentYear)).length;
    const q2Projects = filteredProjects.filter(p => isDateInQuarter(p, 2, currentYear)).length;
    const q3Projects = filteredProjects.filter(p => isDateInQuarter(p, 3, currentYear)).length;
    const q4Projects = filteredProjects.filter(p => isDateInQuarter(p, 4, currentYear)).length;

    // Calculate health metrics
    const onTrackProjects = filteredProjects.filter(p => {
      const health = getFieldValueByLabel(p, 'Project Health');
      return health === 'Green - On Track';
    }).length;

    const offTrackProjects = filteredProjects.filter(p => {
      const health = getFieldValueByLabel(p, 'Project Health');
      return health === 'Yellow - Off Track';
    }).length;

    const atRiskProjects = filteredProjects.filter(p => {
      const health = getFieldValueByLabel(p, 'Project Health');
      return health === 'Red - At Risk';
    }).length;

    const healthNotCompletedProjects = filteredProjects.filter(p => {
      const health = getFieldValueByLabel(p, 'Project Health');
      return !health || health === '';
    }).length;

    return [
      // Top row - 3 metrics
      {
        id: 1,
        label: 'Total Projects',
        value: totalProjects.toString(),
        change: `${closedProjects} completed`,
        changeType: 'positive',
        icon: Target,
        description: 'total projects in system'
      },
      {
        id: 2,
        label: 'Closed Projects',
        value: closedProjects.toString(),
        change: `${Math.round((closedProjects / totalProjects) * 100) || 0}% of total`,
        changeType: 'positive',
        icon: CheckCircle,
        description: 'projects with status "Completed"'
      },
      {
        id: 3,
        label: 'Pushed Projects',
        value: pushedProjects.toString(),
        change: `${Math.round((pushedProjects / totalProjects) * 100) || 0}% of total`,
        changeType: 'positive',
        icon: Play,
        description: 'projects with status "Go-Live Pushed"'
      },
      // Bottom row - 3 metrics
      {
        id: 4,
        label: 'Active Projects',
        value: activeProjects.toString(),
        change: `${Math.round((activeProjects / totalProjects) * 100) || 0}% of total`,
        changeType: 'neutral',
        icon: Clock,
        description: 'projects with status "In progress"'
      },
      {
        id: 5,
        label: 'Not Started',
        value: notStartedProjects.toString(),
        change: `${Math.round((notStartedProjects / totalProjects) * 100) || 0}% of total`,
        changeType: 'neutral',
        icon: Pause,
        description: 'projects with status "Staging"'
      },
      {
        id: 6,
        label: 'Past Due',
        value: pastDueProjects.toString(),
        change: pastDueProjects > 0 ? 'Needs attention' : 'All on track',
        changeType: pastDueProjects > 0 ? 'negative' : 'positive',
        icon: AlertTriangle,
        description: 'active projects past go-live date'
      },
      // Additional row for quarterly metrics
      {
        id: 7,
        label: 'Q1 Projects',
        value: q1Projects.toString(),
        change: `${currentYear} Q1`,
        changeType: 'neutral',
        icon: CalendarDays,
        description: 'projects with Q1 go-live date'
      },
      {
        id: 8,
        label: 'Q2 Projects',
        value: q2Projects.toString(),
        change: `${currentYear} Q2`,
        changeType: 'neutral',
        icon: CalendarDays,
        description: 'projects with Q2 go-live date'
      },
      {
        id: 9,
        label: 'Q3 Projects',
        value: q3Projects.toString(),
        change: `${currentYear} Q3`,
        changeType: 'neutral',
        icon: CalendarDays,
        description: 'projects with Q3 go-live date'
      },
      {
        id: 10,
        label: 'Q4 Projects',
        value: q4Projects.toString(),
        change: `${currentYear} Q4`,
        changeType: 'neutral',
        icon: CalendarDays,
        description: 'projects with Q4 go-live date'
      },
      // Health metrics
      {
        id: 11,
        label: 'On Track',
        value: onTrackProjects.toString(),
        change: `${Math.round((onTrackProjects / totalProjects) * 100) || 0}% of total`,
        changeType: 'positive',
        icon: CheckCircle,
        description: 'projects with health "Green - On Track"'
      },
      {
        id: 12,
        label: 'Off Track',
        value: offTrackProjects.toString(),
        change: `${Math.round((offTrackProjects / totalProjects) * 100) || 0}% of total`,
        changeType: 'neutral',
        icon: AlertCircle,
        description: 'projects with health "Yellow - Off Track"'
      },
      {
        id: 13,
        label: 'At Risk',
        value: atRiskProjects.toString(),
        change: `${Math.round((atRiskProjects / totalProjects) * 100) || 0}% of total`,
        changeType: 'negative',
        icon: AlertTriangle,
        description: 'projects with health "Red - At Risk"'
      },
      {
        id: 14,
        label: 'Health Not Completed',
        value: healthNotCompletedProjects.toString(),
        change: `${Math.round((healthNotCompletedProjects / totalProjects) * 100) || 0}% of total`,
        changeType: 'neutral',
        icon: HelpCircle,
        description: 'projects with missing health status'
      }
    ];
  }, [dashboardData?.details?.projects, filteredProjects]);

  // Handle metric click to open modal
  const handleMetricClick = (metricId, metricLabel) => {
    if (!dashboardData?.details?.projects) return;

    let projects = [];
    let metricType = '';

    switch (metricId) {
      case 1: // Total Projects
        projects = filteredProjects;
        metricType = 'all';
        break;
      case 2: // Closed Projects
        projects = filteredProjects.filter(p => p.status?.label === 'Completed');
        metricType = 'status';
        break;
      case 3: // Pushed Projects
        projects = filteredProjects.filter(p => p.status?.label === 'Go-Live Pushed');
        metricType = 'status';
        break;
      case 4: // Active Projects
        projects = filteredProjects.filter(p => p.status?.label === 'In progress');
        metricType = 'status';
        break;
      case 5: // Projects Not Started
        projects = filteredProjects.filter(p => p.status?.label === 'Staging');
        metricType = 'status';
        break;
      case 6: // Projects Past Due
        projects = filteredProjects.filter(p => isProjectPastDue(p));
        metricType = 'status';
        break;
      case 7: // Q1 Projects
        projects = filteredProjects.filter(p => isDateInQuarter(p, 1, currentYear));
        metricType = 'quarter';
        break;
      case 8: // Q2 Projects
        projects = filteredProjects.filter(p => isDateInQuarter(p, 2, currentYear));
        metricType = 'quarter';
        break;
      case 9: // Q3 Projects
        projects = filteredProjects.filter(p => isDateInQuarter(p, 3, currentYear));
        metricType = 'quarter';
        break;
      case 10: // Q4 Projects
        projects = filteredProjects.filter(p => isDateInQuarter(p, 4, currentYear));
        metricType = 'quarter';
        break;
      case 11: // On Track
        projects = filteredProjects.filter(p => {
          const health = getFieldValueByLabel(p, 'Project Health');
          return health === 'Green - On Track';
        });
        metricType = 'health';
        break;
      case 12: // Off Track
        projects = filteredProjects.filter(p => {
          const health = getFieldValueByLabel(p, 'Project Health');
          return health === 'Yellow - Off Track';
        });
        metricType = 'health';
        break;
      case 13: // At Risk
        projects = filteredProjects.filter(p => {
          const health = getFieldValueByLabel(p, 'Project Health');
          return health === 'Red - At Risk';
        });
        metricType = 'health';
        break;
      case 14: // Health Not Completed
        projects = filteredProjects.filter(p => {
          const health = getFieldValueByLabel(p, 'Project Health');
          return !health || health === '';
        });
        metricType = 'health';
        break;
      default:
        return;
    }

    setModalState({
      isOpen: true,
      title: `${metricLabel} (${projects.length} projects)`,
      projects,
      metricType
    });
  };

  const closeModal = () => {
    setModalState({
      isOpen: false,
      title: '',
      projects: [],
      metricType: ''
    });
  };

  // Memoize change icon component
  const getChangeIcon = useMemo(() => (changeType) => {
    switch (changeType) {
      case 'positive':
        return <TrendingUp size={16} />;
      case 'negative':
        return <TrendingDown size={16} />;
      default:
        return <Minus size={16} />;
    }
  }, []);

  if (loading) {
    return <div className="loading">Loading metrics...</div>;
  }

  if (!dashboardData) {
    return <div className="loading">No data available</div>;
  }

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">Key Metrics Overview</h2>
        <div className="card-actions">
          <div className="filter-controls">
            <MultiSelect
              label="Project Type"
              options={projectTypes}
              selectedValues={projectTypeFilter}
              onChange={setProjectTypeFilter}
              placeholder="All Project Types"
            />
            <MultiSelect
              label="Project Subtype"
              options={projectSubtypes}
              selectedValues={projectSubtypeFilter}
              onChange={setProjectSubtypeFilter}
              placeholder="All Project Subtypes"
            />

          </div>
        </div>
      </div>
      
      <div className="metric-grid-3x2">
        {/* Row 1: Total, Closed, Pushed, Active, Not Started, Past Due */}
        <div className="metric-row metric-row-1">
          {metricsData.slice(0, 6).map((metric) => {
            const IconComponent = metric.icon;
            return (
              <div 
                key={metric.id} 
                className="metric-card clickable"
                onClick={() => handleMetricClick(metric.id, metric.label)}
                title={`Click to view ${metric.label} projects`}
              >
                <div className="metric-header">
                  <IconComponent size={20} color="#64748b" />
                  <span className="metric-label">{metric.label}</span>
                </div>
                <div className="metric-value">{metric.value}</div>
                <div className={`metric-change ${metric.changeType}`}>
                  {getChangeIcon(metric.changeType)}
                  <span>{metric.change}</span>
                  <span className="metric-description">{metric.description}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Row 2: Q1, Q2, Q3, Q4 */}
        <div className="metric-row metric-row-2">
          {metricsData.slice(6, 10).map((metric) => {
            const IconComponent = metric.icon;
            return (
              <div 
                key={metric.id} 
                className="metric-card clickable"
                onClick={() => handleMetricClick(metric.id, metric.label)}
                title={`Click to view ${metric.label} projects`}
              >
                <div className="metric-header">
                  <IconComponent size={20} color="#64748b" />
                  <span className="metric-label">{metric.label}</span>
                </div>
                <div className="metric-value">{metric.value}</div>
                <div className={`metric-change ${metric.changeType}`}>
                  {getChangeIcon(metric.changeType)}
                  <span>{metric.change}</span>
                  <span className="metric-description">{metric.description}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Row 3: On Track, Off Track, At Risk, Health Not Completed */}
        <div className="metric-row metric-row-3">
          {metricsData.slice(10, 14).map((metric) => {
            const IconComponent = metric.icon;
            return (
              <div 
                key={metric.id} 
                className="metric-card clickable"
                onClick={() => handleMetricClick(metric.id, metric.label)}
                title={`Click to view ${metric.label} projects`}
              >
                <div className="metric-header">
                  <IconComponent size={20} color="#64748b" />
                  <span className="metric-label">{metric.label}</span>
                </div>
                <div className="metric-value">{metric.value}</div>
                <div className={`metric-change ${metric.changeType}`}>
                  {getChangeIcon(metric.changeType)}
                  <span>{metric.change}</span>
                  <span className="metric-description">{metric.description}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <ProjectModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        title={modalState.title}
        projects={modalState.projects}
        metricType={modalState.metricType}
      />
    </div>
  );
};

export default MetricsOverview;
